/**
 * Supabase client configuration for frontend
 * Replaces Firebase authentication
 */

import { createClient } from '@supabase/supabase-js'

// Get Supabase configuration from environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:54321'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0'

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: window?.localStorage,
    storageKey: 'agentbuilder-auth'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
})

// Export types for type safety
export type Database = {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          firebase_uid: string | null
          email: string
          display_name: string | null
          avatar_url: string | null
          role: string
          groups: string[]
          permissions: string[]
          platform_access: string[]
          is_active: boolean
          is_verified: boolean
          email_verified: boolean
          account_status: string
          subscription_tier: string
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          firebase_uid?: string | null
          email: string
          display_name?: string | null
          avatar_url?: string | null
          role?: string
          groups?: string[]
          permissions?: string[]
          platform_access?: string[]
          is_active?: boolean
          is_verified?: boolean
          email_verified?: boolean
          account_status?: string
          subscription_tier?: string
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          firebase_uid?: string | null
          email?: string
          display_name?: string | null
          avatar_url?: string | null
          role?: string
          groups?: string[]
          permissions?: string[]
          platform_access?: string[]
          is_active?: boolean
          is_verified?: boolean
          email_verified?: boolean
          account_status?: string
          subscription_tier?: string
          created_at?: string
          updated_at?: string
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
  }
}

export type Profile = Database['public']['Tables']['profiles']['Row']

// Helper functions for authentication
export const authHelpers = {
  /**
   * Sign up with email and password
   */
  async signUp(email: string, password: string, metadata?: { full_name?: string }) {
    return await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    })
  },

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string) {
    return await supabase.auth.signInWithPassword({
      email,
      password
    })
  },

  /**
   * Sign in with OAuth provider
   */
  async signInWithProvider(provider: 'google' | 'github') {
    return await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: `${window.location.origin}/auth/callback`
      }
    })
  },

  /**
   * Sign out
   */
  async signOut() {
    return await supabase.auth.signOut()
  },

  /**
   * Get current session
   */
  async getSession() {
    return await supabase.auth.getSession()
  },

  /**
   * Get current user
   */
  async getUser() {
    return await supabase.auth.getUser()
  },

  /**
   * Reset password
   */
  async resetPassword(email: string) {
    return await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`
    })
  },

  /**
   * Update password
   */
  async updatePassword(password: string) {
    return await supabase.auth.updateUser({ password })
  },

  /**
   * Update user metadata
   */
  async updateUser(updates: { email?: string; data?: any }) {
    return await supabase.auth.updateUser(updates)
  }
}

// Profile helpers for MongoDB sync
export const profileHelpers = {
  /**
   * Get user profile from Supabase
   */
  async getProfile(userId: string): Promise<Profile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()

    if (error) {
      console.error('Error fetching profile:', error)
      return null
    }

    return data
  },

  /**
   * Create user profile
   */
  async createProfile(profile: Database['public']['Tables']['profiles']['Insert']) {
    const { data, error } = await supabase
      .from('profiles')
      .insert(profile)
      .select()
      .single()

    if (error) {
      console.error('Error creating profile:', error)
      throw error
    }

    return data
  },

  /**
   * Update user profile
   */
  async updateProfile(userId: string, updates: Database['public']['Tables']['profiles']['Update']) {
    const { data, error } = await supabase
      .from('profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .select()
      .single()

    if (error) {
      console.error('Error updating profile:', error)
      throw error
    }

    return data
  }
}

// Realtime subscriptions
export const realtimeHelpers = {
  /**
   * Subscribe to profile changes
   */
  subscribeToProfile(userId: string, callback: (payload: any) => void) {
    return supabase
      .channel(`profile:${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${userId}`
        },
        callback
      )
      .subscribe()
  }
}

export default supabase